import { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseIcon from "@mui/icons-material/Close";
import { FoodsIcon } from "@foodpilot/foods";
import { Box, Button, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
    getConsentGovernanceErrorMessage,
    useDeleteAdminConsentItem,
    useUpsertAdminConsentItem,
    useUpsertAdminConsentItemVersion,
} from "src/api/client-api/consent/useManageConsentGovernanceAdmin";
import { type ConsentGovernanceItem } from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import { type Locale, useGetDefaultLocale } from "src/api/locales/useLocales";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { useSnackbar } from "src/utils/useSnackbar";
import { ConsentLabelsStep, ConsentTypeCategoryStep, ConsentVersionStep } from "./ConsentCreationDrawerSteps";
import { ConsentCreationStepper, ConsentSummaryStep } from "./ConsentCreationDrawerSummary";
import {
    type ConsentCreationState,
    type ConsentCreationType,
    type ConsentDraftLabel,
    type ConsentDraftVersion,
    CONSENT_CREATION_STEP_KEYS,
    buildConsentCreationItemPayload,
    buildGeneratedTitle,
    buildInitialConsentCreationState,
    buildStepErrors,
    canAdvanceConsentCreationStep,
    getCompletedLocaleCodes,
    getErrorMessage,
    getSuccessMessage,
    getTypeLabel,
    isCategoryCompatibleWithType,
    normalizeOptionalString,
} from "./consentCreationShared";

type ConsentCreationDrawerProps = {
    initialLocale: string;
    initialType: ConsentCreationType;
    localeOptions: Locale[];
    onClose: () => void;
    onCreated?: (payload: { item: ConsentGovernanceItem; visibleLocale: string }) => void;
    open: boolean;
};

export const ConsentCreationDrawer = ({
    initialLocale,
    initialType,
    localeOptions,
    onClose,
    onCreated,
    open,
}: ConsentCreationDrawerProps) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const { isMobile, isTablet } = useResponsiveBreakpoints();
    const snackbar = useSnackbar();
    const { data: defaultLocaleData } = useGetDefaultLocale();
    const upsertItemMutation = useUpsertAdminConsentItem();
    const upsertVersionMutation = useUpsertAdminConsentItemVersion();
    const deleteItemMutation = useDeleteAdminConsentItem();
    const uiLocale = i18n.resolvedLanguage || i18n.language || "en";
    const defaultLocale =
        localeOptions.find((locale) => locale.isDefault)?.code ??
        defaultLocaleData?.code ??
        localeOptions.find((locale) => locale.code === initialLocale)?.code ??
        localeOptions[0]?.code ??
        "en";
    const buildInitialState = useCallback(
        () => ({
            ...buildInitialConsentCreationState(localeOptions, defaultLocale, initialType),
            versionNote: t("settings.consent.document_wizard.initial_version_note", "Initial version"),
        }),
        [defaultLocale, initialType, localeOptions, t],
    );
    const [activeStep, setActiveStep] = useState(0);
    const [activeLocale, setActiveLocale] = useState(defaultLocale);
    const [state, setState] = useState<ConsentCreationState>(buildInitialState);

    useEffect(() => {
        if (!open) {
            return;
        }

        setActiveStep(0);
        setActiveLocale(defaultLocale);
        setState(buildInitialState());
    }, [buildInitialState, defaultLocale, open]);

    const currentLabelDraft = state.labelDrafts[activeLocale] ?? { description: "", name: "" };
    const currentVersionDraft = state.versionDrafts[activeLocale] ?? { content: "", documentUrl: "" };
    const isCurrentLocaleDefault = activeLocale === defaultLocale;
    const summaryLocale =
        state.labelDrafts[state.lastTouchedLocale]?.name.trim().length ? state.lastTouchedLocale : defaultLocale;
    const summaryLabelDraft = state.labelDrafts[summaryLocale] ?? { description: "", name: "" };
    const completedLocaleCodes = getCompletedLocaleCodes(state, localeOptions);
    const errors = useMemo(() => buildStepErrors(state, defaultLocale, t), [defaultLocale, state, t]);
    const canAdvance = canAdvanceConsentCreationStep(activeStep, errors);
    const maxAccessibleStep = useMemo(() => {
        if (!canAdvanceConsentCreationStep(0, errors)) {
            return 0;
        }

        if (!canAdvanceConsentCreationStep(1, errors)) {
            return 1;
        }

        if (!canAdvanceConsentCreationStep(2, errors)) {
            return 2;
        }

        return 3;
    }, [errors]);
    const isSaving = upsertItemMutation.isLoading || upsertVersionMutation.isLoading || deleteItemMutation.isLoading;
    const publicationPreview = new Intl.DateTimeFormat(uiLocale, {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(new Date());
    const primaryColor = theme.palette.primary.main;
    const wizardAccentColor = theme.palette.primary.main;
    const subtitleValue = getTypeLabel(state.type, t);

    const drawerWidth =
        isMobile ? "100vw"
        : isTablet ? "min(100vw, 980px)"
        : "min(100vw, 1240px)";

    const setLabelDraft = (field: keyof ConsentDraftLabel, value: string) => {
        setState((prev) => ({
            ...prev,
            lastTouchedLocale: activeLocale,
            labelDrafts: {
                ...prev.labelDrafts,
                [activeLocale]: {
                    ...(prev.labelDrafts[activeLocale] ?? { description: "", name: "" }),
                    [field]: value,
                },
            },
        }));
    };

    const setVersionDraft = (field: keyof ConsentDraftVersion, value: string) => {
        setState((prev) => ({
            ...prev,
            lastTouchedLocale: activeLocale,
            versionDrafts: {
                ...prev.versionDrafts,
                [activeLocale]: {
                    ...(prev.versionDrafts[activeLocale] ?? { content: "", documentUrl: "" }),
                    [field]: value,
                },
            },
        }));
    };

    const handleClose = () => {
        if (isSaving) {
            return;
        }

        onClose();
    };

    const handleTypeChange = (value: ConsentCreationType) => {
        setState((prev) => ({
            ...prev,
            category: isCategoryCompatibleWithType(value, prev.category) ? prev.category : "",
            type: value,
        }));
    };

    const handleStepSelection = useCallback(
        (targetStep: number) => {
            if (targetStep < 0 || targetStep > maxAccessibleStep) {
                return;
            }

            setActiveStep(targetStep);
        },
        [maxAccessibleStep],
    );

    const handleCreate = async () => {
        const baseLabelDraft = state.labelDrafts[defaultLocale] ?? { description: "", name: "" };
        const baseVersionDraft = state.versionDrafts[defaultLocale] ?? { content: "", documentUrl: "" };
        let lastSuccessfulLocale = defaultLocale;
        const partialFailureLocales: string[] = [];

        try {
            const createdItem = await upsertItemMutation.mutateAsync({
                locale: defaultLocale,
                data: {
                    ...buildConsentCreationItemPayload(state, baseLabelDraft),
                },
            });

            const createdVersionId = await (async () => {
                try {
                    const createdVersion = await upsertVersionMutation.mutateAsync({
                        locale: defaultLocale,
                        data: {
                            consentItem: createdItem["@id"] as IRI,
                            version: state.version.trim(),
                            title: buildGeneratedTitle(baseLabelDraft.name),
                            summary: null,
                            content: normalizeOptionalString(baseVersionDraft.content),
                            documentUrl: normalizeOptionalString(baseVersionDraft.documentUrl),
                            published: true,
                        },
                    });

                    return createdVersion.id;
                } catch (error) {
                    await deleteItemMutation.mutateAsync(createdItem.id).catch(() => undefined);
                    throw error;
                }
            })();

            const localesToPersist = completedLocaleCodes.filter((localeCode) => localeCode !== defaultLocale);

            for (const localeCode of localesToPersist) {
                const labelDraft = state.labelDrafts[localeCode];
                const versionDraft = state.versionDrafts[localeCode];

                try {
                    await upsertItemMutation.mutateAsync({
                        id: createdItem.id,
                        locale: localeCode,
                        data: {
                            ...buildConsentCreationItemPayload(state, labelDraft),
                        },
                    });

                    await upsertVersionMutation.mutateAsync({
                        id: createdVersionId,
                        locale: localeCode,
                        data: {
                            consentItem: createdItem["@id"] as IRI,
                            version: state.version.trim(),
                            title: buildGeneratedTitle(labelDraft.name),
                            summary: null,
                            content: normalizeOptionalString(versionDraft.content),
                            documentUrl: normalizeOptionalString(versionDraft.documentUrl),
                            published: true,
                        },
                    });

                    lastSuccessfulLocale = localeCode;
                } catch {
                    partialFailureLocales.push(localeCode);
                }
            }

            snackbar.setSnackbarProps({
                message: getSuccessMessage(state.type, partialFailureLocales.length > 0, t),
                type: partialFailureLocales.length > 0 ? "warning" : "success",
            });

            onCreated?.({
                item: createdItem,
                visibleLocale:
                    (
                        completedLocaleCodes.includes(state.lastTouchedLocale) &&
                        !partialFailureLocales.includes(state.lastTouchedLocale)
                    ) ?
                        state.lastTouchedLocale
                    :   lastSuccessfulLocale,
            });
            onClose();
        } catch (error) {
            const fallbackMessage = getErrorMessage(state.type, t);

            snackbar.setSnackbarProps({
                message: getConsentGovernanceErrorMessage(error, t, fallbackMessage),
                type: "error",
            });
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            ModalProps={{ keepMounted: true }}
            slotProps={{
                paper: {
                    sx: {
                        width: drawerWidth,
                        maxWidth: "100vw",
                        height: "100dvh",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: theme.palette.common.white,
                    },
                },
            }}
        >
            <Box sx={{ flexShrink: 0 }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={3}
                    sx={{
                        px: { xs: 3, md: 5 },
                        py: { xs: 3, md: 4 },
                        borderBottom: `1px solid ${theme.custom.grey[500]}`,
                    }}
                >
                    <Stack direction="row" gap={2.5} alignItems="center">
                        <Box
                            sx={{
                                width: { xs: 42, md: 44 },
                                height: { xs: 42, md: 44 },
                                borderRadius: 2,
                                bgcolor: alpha(wizardAccentColor, 0.12),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                color: wizardAccentColor,
                            }}
                        >
                            <FoodsIcon icon="wand" size={1.65} />
                        </Box>
                        <Box>
                            <Typography variant="body" sx={{ fontWeight: 700 }}>
                                {t("settings.consent.document_wizard.title", "New purpose")}
                            </Typography>
                            <Typography
                                variant="body"
                                sx={{
                                    color: theme.custom.grey[2000],
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                    alignItems: "center",
                                }}
                            >
                                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                                    {subtitleValue}
                                </Box>
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton
                        onClick={handleClose}
                        disabled={isSaving}
                        sx={{
                            border: `1px solid ${theme.custom.grey[500]}`,
                            borderRadius: 2,
                            width: { xs: 38, md: 40 },
                            height: { xs: 38, md: 40 },
                            p: 0,
                            bgcolor: theme.palette.common.white,
                        }}
                    >
                        <CloseIcon sx={{ fontSize: "1.3rem" }} />
                    </IconButton>
                </Stack>
                <ConsentCreationStepper
                    activeStep={activeStep}
                    maxAccessibleStep={maxAccessibleStep}
                    onStepClick={handleStepSelection}
                />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
                {activeStep === 0 && (
                    <ConsentTypeCategoryStep
                        errors={{ category: errors.category, code: errors.code }}
                        state={state}
                        onCategoryChange={(value) => setState((prev) => ({ ...prev, category: value }))}
                        onCodeChange={(value) => setState((prev) => ({ ...prev, code: value }))}
                        onTypeChange={handleTypeChange}
                    />
                )}
                {activeStep === 1 && (
                    <ConsentLabelsStep
                        activeLocale={activeLocale}
                        currentLabelDraft={currentLabelDraft}
                        errors={{ description: errors.description, name: errors.name }}
                        isCurrentLocaleDefault={isCurrentLocaleDefault}
                        localeOptions={localeOptions}
                        setActiveLocale={setActiveLocale}
                        state={state}
                        onDescriptionChange={(value) => setLabelDraft("description", value)}
                        onNameChange={(value) => setLabelDraft("name", value)}
                    />
                )}
                {activeStep === 2 && (
                    <ConsentVersionStep
                        activeLocale={activeLocale}
                        currentLabelDraft={currentLabelDraft}
                        currentVersionDraft={currentVersionDraft}
                        errors={{ content: errors.content, documentUrl: errors.documentUrl, version: errors.version }}
                        isCurrentLocaleDefault={isCurrentLocaleDefault}
                        localeOptions={localeOptions}
                        publicationPreview={publicationPreview}
                        setActiveLocale={setActiveLocale}
                        state={state}
                        onContentChange={(value) => setVersionDraft("content", value)}
                        onDocumentUrlChange={(value) => setVersionDraft("documentUrl", value)}
                        onVersionChange={(value) => setState((prev) => ({ ...prev, version: value }))}
                        onVersionNoteChange={(value) => setState((prev) => ({ ...prev, versionNote: value }))}
                    />
                )}
                {activeStep === 3 && (
                    <ConsentSummaryStep
                        completedLocaleCodes={completedLocaleCodes}
                        state={state}
                        summaryLabelDraft={summaryLabelDraft}
                    />
                )}
            </Box>

            <Box
                sx={{
                    flexShrink: 0,
                    borderTop: `1px solid ${theme.custom.grey[500]}`,
                    px: { xs: 3, md: 5 },
                    py: 3,
                    bgcolor: theme.palette.common.white,
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                    <Button
                        variant="outlined"
                        onClick={() => (activeStep === 0 ? handleClose() : setActiveStep((prev) => prev - 1))}
                        disabled={isSaving}
                        startIcon={activeStep === 0 ? undefined : <ArrowBackIcon />}
                        sx={{
                            minWidth: 170,
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 700,
                            px: 3,
                            py: 1.5,
                            borderColor: theme.custom.grey[500],
                            color: theme.palette.text.primary,
                        }}
                    >
                        {activeStep === 0 ?
                            t("common.actions.cancel", "Cancel")
                        :   t("settings.consent.document_wizard.previous", "Previous")}
                    </Button>
                    {activeStep < CONSENT_CREATION_STEP_KEYS.length - 1 ?
                        <Button
                            variant="contained"
                            onClick={() => setActiveStep((prev) => prev + 1)}
                            disabled={!canAdvance || isSaving}
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                minWidth: 200,
                                borderRadius: 999,
                                textTransform: "none",
                                fontWeight: 700,
                                px: 3,
                                py: 1.5,
                                bgcolor: primaryColor,
                                color: theme.palette.common.white,
                                boxShadow: "none",
                                "&:hover": {
                                    bgcolor: theme.palette.primary.dark,
                                    boxShadow: "none",
                                },
                            }}
                        >
                            {t("settings.consent.document_wizard.next", "Next")}
                        </Button>
                    :   <Button
                            variant="contained"
                            onClick={handleCreate}
                            disabled={isSaving}
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                minWidth: 230,
                                borderRadius: 999,
                                textTransform: "none",
                                fontWeight: 700,
                                px: 3,
                                py: 1.5,
                                bgcolor: theme.palette.success.main,
                                color: theme.palette.common.white,
                                boxShadow: "none",
                                "&:hover": {
                                    bgcolor: theme.palette.success.dark,
                                    boxShadow: "none",
                                },
                            }}
                        >
                            {t("settings.consent.document_wizard.create", "Create purpose")}
                        </Button>
                    }
                </Stack>
            </Box>
        </Drawer>
    );
};
